import { useState, useEffect } from 'react'
import axios from 'axios'

import { logger } from '@/utils/logger'

interface Session {
  id: number
  browser: string
  os: string
  device_type: string
  ip_address: string
  created_at: string
  last_activity: string
  is_current: boolean
}

const deviceIcons: Record<string, string> = {
  mobile: 'M17 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM11 18h2',
  tablet: 'M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM11 18h2',
  desktop: 'M3 4h18v12H3zM8 20h8M12 16v4',
}

function DeviceIcon({ deviceType }: { deviceType: string }) {
  const d = deviceIcons[deviceType] || deviceIcons.desktop
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

export default function SessionsTab() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [revokingId, setRevokingId] = useState<number | null>(null)
  const [revokingAll, setRevokingAll] = useState(false)
  const [error, setError] = useState('')

  const loadSessions = async () => {
    try {
      setIsLoading(true)
      setError('')
      const response = await axios.get('/api/v1/auth/sessions', { withCredentials: true })
      setSessions(response.data.sessions || [])
    } catch (error: any) {
      setError('Sitzungen konnten nicht geladen werden')
      logger.error('Error loading sessions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSessions()
  }, [])

  const getCsrfToken = () =>
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrf_token='))
      ?.split('=')[1]

  const handleRevokeSession = async (sessionId: number) => {
    if (!confirm('Möchten Sie diese Session wirklich beenden?')) {
      return
    }

    try {
      setRevokingId(sessionId)
      setError('')
      await axios.delete(`/api/v1/auth/sessions/${sessionId}`, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': getCsrfToken() },
      })
      await loadSessions()
    } catch (error: any) {
      setError('Sitzung konnte nicht beendet werden')
      logger.error('Error revoking session:', error)
    } finally {
      setRevokingId(null)
    }
  }

  const handleRevokeAllSessions = async () => {
    if (!confirm('Möchten Sie wirklich alle anderen Sessions beenden? Sie bleiben nur auf diesem Gerät angemeldet.')) {
      return
    }

    try {
      setRevokingAll(true)
      setError('')
      await axios.post(
        '/api/v1/auth/sessions/logout-all',
        {},
        { withCredentials: true, headers: { 'X-CSRF-Token': getCsrfToken() } }
      )
      await loadSessions()
    } catch (error: any) {
      setError('Sitzungen konnten nicht beendet werden')
      logger.error('Error revoking all sessions:', error)
    } finally {
      setRevokingAll(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Gerade eben'
    if (diffMins < 60) return `Vor ${diffMins} Minute${diffMins !== 1 ? 'n' : ''}`
    if (diffHours < 24) return `Vor ${diffHours} Stunde${diffHours !== 1 ? 'n' : ''}`
    if (diffDays < 7) return `Vor ${diffDays} Tag${diffDays !== 1 ? 'en' : ''}`

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <div className="wc-card wc-elev-sm" style={{ gap: 18 }}>
        <div>
          <h3 className="wc-card-title" style={{ margin: 0 }}>Aktive Sitzungen</h3>
          <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>Verwalten Sie Ihre aktiven Anmeldesitzungen</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
          <div className="wc-spinner" />
        </div>
      </div>
    )
  }

  const currentSession = sessions.find((s) => s.is_current)
  const otherSessions = sessions.filter((s) => !s.is_current)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="wc-card wc-elev-sm" style={{ gap: 18 }}>
        <div>
          <h3 className="wc-card-title" style={{ margin: 0 }}>Aktive Sitzungen</h3>
          <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
            Verwalten Sie Ihre aktiven Anmeldesitzungen auf verschiedenen Geräten
          </p>
        </div>

        {error && <div className="wc-alert wc-alert-error">{error}</div>}

        {currentSession && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              padding: 14,
              borderRadius: 'var(--wc-radius-md)',
              border: '1px solid var(--wc-divider)',
              background: 'color-mix(in srgb, var(--wc-accent) 8%, transparent)',
            }}
          >
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              <DeviceIcon deviceType={currentSession.device_type} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 14 }}>{currentSession.browser} auf {currentSession.os}</span>
                <span className="wc-tag wc-tag-accent">Aktuelle Sitzung</span>
              </div>
              <div className="wc-muted" style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span>IP-Adresse: {currentSession.ip_address}</span>
                <span>Angemeldet: {formatDate(currentSession.created_at)}</span>
                <span>Letzte Aktivität: {formatDate(currentSession.last_activity)}</span>
              </div>
            </div>
          </div>
        )}

        {otherSessions.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
              <h4 style={{ margin: 0, fontSize: 14 }}>Andere Sitzungen</h4>
              <button
                onClick={handleRevokeAllSessions}
                disabled={revokingAll}
                className="wc-btn wc-btn-secondary"
                style={{ padding: '6px 12px', fontSize: 12, color: '#fca5a5' }}
              >
                {revokingAll ? 'Beende...' : 'Alle anderen beenden'}
              </button>
            </div>

            {otherSessions.map((session) => (
              <div
                key={session.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: 14,
                  borderRadius: 'var(--wc-radius-md)',
                  border: '1px solid var(--wc-divider)',
                }}
              >
                <div style={{ flexShrink: 0, marginTop: 2 }}>
                  <DeviceIcon deviceType={session.device_type} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, marginBottom: 4 }}>{session.browser} auf {session.os}</div>
                  <div className="wc-muted" style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>IP-Adresse: {session.ip_address}</span>
                    <span>Angemeldet: {formatDate(session.created_at)}</span>
                    <span>Letzte Aktivität: {formatDate(session.last_activity)}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeSession(session.id)}
                  disabled={revokingId === session.id}
                  className="wc-btn wc-btn-ghost"
                  style={{ color: '#fca5a5', flexShrink: 0 }}
                >
                  Beenden
                </button>
              </div>
            ))}
          </>
        )}

        {sessions.length === 1 && (
          <p className="wc-muted" style={{ textAlign: 'center', fontSize: 13, margin: '8px 0' }}>
            Sie haben aktuell keine weiteren aktiven Sitzungen
          </p>
        )}
      </div>

      <div className="wc-alert wc-alert-info">
        <strong>Sicherheitshinweis:</strong> Wenn Sie eine Sitzung nicht erkennen, beenden Sie diese sofort und ändern Sie Ihr Passwort.
      </div>
    </div>
  )
}
